import { Product } from '../../../utils/types/Product'

export interface BestSellerConfig {
  /**
   * Percentage of products that should receive best-seller tags (0-1)
   * Default: 0.2 (20%)
   */
  percentage: number
}

export const DEFAULT_BEST_SELLER_CONFIG: BestSellerConfig = {
  percentage: 0.2, // 20% of products should be marked as best-sellers
}

/**
 * Simple and reliable hash function for deterministic best-seller assignment
 * Uses a basic string hash with good distribution properties
 *
 * @param str - String to hash
 * @returns Hash value as number between 0 and 1
 */
const hashStringToFloat = (str: string): number => {
  let hash = 0
  if (str.length === 0) return hash

  for (let i = 0; i < str.length; i += 1) {
    const char = str.charCodeAt(i)
    // 31 is a prime number that gives good distribution when hashing - it spreads values out evenly
    hash = (hash * 31) + char
    // eslint-disable-next-line no-bitwise
    hash &= hash // Convert to 32-bit integer
  }

  // Convert to positive number and normalize to 0-1 range
  return Math.abs(hash % 10000) / 10000
}

/**
 * Determines if a product should display a best-seller tag based on deterministic algorithm
 * Uses product ID as seed for consistent results across page loads
 *
 * @param product - Product object containing ID
 * @param config - Configuration for best-seller percentage (optional)
 * @returns true if product should show best-seller tag, false otherwise
 */
export const shouldShowBestSellerTag = (
  product: Product,
  config: BestSellerConfig = DEFAULT_BEST_SELLER_CONFIG,
): boolean => {
  const productId = product['product.id'] || product.id

  if (!productId) {
    return false
  }

  // Generate normalized hash from product ID (0-1 range)
  const normalizedValue = hashStringToFloat(productId)

  // Return true if the normalized value falls within the configured percentage
  return normalizedValue < config.percentage
}

/**
 * Get statistics about best-seller tag distribution for a list of products
 * Useful for testing and validation
 *
 * @param products - Array of products to analyze
 * @param config - Configuration for best-seller percentage (optional)
 * @returns Statistics object with counts and percentages
 */
export const getBestSellerStats = (
  products: Product[],
  config: BestSellerConfig = DEFAULT_BEST_SELLER_CONFIG,
) => {
  const bestSellerCount = products.filter(
    (product) => shouldShowBestSellerTag(product, config),
  ).length

  const totalCount = products.length
  const actualPercentage = totalCount > 0 ? bestSellerCount / totalCount : 0

  return {
    bestSellerCount,
    totalCount,
    actualPercentage,
    targetPercentage: config.percentage,
    difference: actualPercentage - config.percentage,
  }
}

export const libraryFunctions = {
  shouldShowBestSellerTag,
  getBestSellerStats,
}
